import { useQuery } from "@tanstack/react-query";
import { Alert, Button, DatePicker, Descriptions, Form, Input, Pagination, Select, Space, Table, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { Link } from "react-router-dom";
import { hasFeature } from "../../lib/features";
import { hasRole } from "../../lib/roles";
import { useApi, useMe } from "../../lib/useApi";
import { PageHeader, SidePanel } from "../../ui";
import { useMailScope } from "./mailScope";
import type { MailInboundDetail, MailInboundItem, MailInboundSearch, MailInboundStats } from "./mailTypes";
import { mailError } from "./mailUtils";

const PER_PAGE = 50;
const MAX_PAGE = 200; // API の page 上限(dlp-inbound-log §7)
const MAX_DAYS = 366;

/** 欠落計数(DlpJobState mail_inbound_stats)のキー → 意味(設計 §3.5 の表)。未知のキーはキー名のまま出す。 */
const MISSED_LABELS: Record<string, string> = {
  data_deadline: "本文受信の期限切れ(相手が再送)",
  data_oversize: "DATA 実バイト超過(観測できた DATA 超過回数)",
  data_line_too_long: "行長超過",
  data_disconnected: "本文の途中か保存前の切断(相手が再送)",
  store_unavailable: "保存不可で 451 を返した回数(相手が再送)",
  rcpt_unavailable: "受付不可で 451 を返した回数(宛先の段階。相手が再送)",
  rcpt_mixed: "受付不可で 451 を返した回数(記録アドレスと他の宛先の混在。相手が再送)",
  "inbound_unavailable:restore": "受付不可で 451 を返した回数(復元作業中。相手が再送)",
  "inbound_unavailable:exception": "受付不可で 451 を返した回数(処理の例外。相手が再送)",
  "inbound_unavailable:busy": "受付不可で 451 を返した回数(混雑・受信枠不足。相手が再送)",
  "inbound_unavailable:feature": "受付不可で 451 を返した回数(機能無効・ライセンス不可。相手が再送)",
  mail_size_rejected: "SIZE 宣言で拒否した回数(流路不明)",
  reply_lost: "保存済みで応答の送出完了を確認できない回数(欠落ではない)",
};
type MissedGroup = "missed" | "size" | "reply" | "other";
function missedGroup(name: string): MissedGroup {
  if (name === "mail_size_rejected") return "size";
  if (name === "reply_lost") return "reply";
  if (name in MISSED_LABELS || name.startsWith("inbound_unavailable:")) return "missed";
  return "other";
}
function missedLabel(name: string) {
  return MISSED_LABELS[name] ?? (name.startsWith("inbound_unavailable:") ? `受付不可で 451 を返した回数(${name.slice("inbound_unavailable:".length)}。相手が再送)` : name);
}
const MISSED_GROUPS: { group: MissedGroup; title: string; note: string }[] = [
  { group: "missed", title: "行が作られなかったことを観測できた回数(欠落)", note: "観測できた DATA 超過回数・受付不可で 451 を返した回数・保存不可で 451 を返した回数などです。観測できない欠落(接続元外・経路の遮断)は含まず、どのメールが欠けたかも分かりません。" },
  { group: "size", title: "SIZE 宣言で拒否した回数(流路不明)", note: "MAIL の SIZE 宣言が上限を超えたため 552 で拒否した回数です。拒否した時点では記録用の複製かどうか分からないため、欠落の回数とは分けています。" },
  { group: "reply", title: "保存済みだが応答の送出完了を確認できない回数(欠落ではない)", note: "記録の行は保存されています。相手が再送しても重複は取り除かれます。" },
  { group: "other", title: "その他の計数(未分類)", note: "この画面が意味を知らない計数です。キー名のまま表示しています。" },
];

function MissedTables({ stats }: { stats: MailInboundStats }) {
  const names = [...new Set([...Object.keys(stats.missed.total), ...Object.values(stats.missed.days).flatMap((d) => Object.keys(d))])].sort();
  const rows = [...Object.entries(stats.missed.days).map(([date, counts]) => ({ date, counts })).reverse(), { date: "累計(全期間)", counts: stats.missed.total }];
  return <>
    <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>以下は gateway が観測できた回数だけを数えます。「超過メール件数」ではありません。日ごとの内訳は直近 31 日分だけ残ります。</Typography.Paragraph>
    {MISSED_GROUPS.map(({ group, title, note }) => {
      const cols = names.filter((name) => missedGroup(name) === group);
      if (group === "other" && !cols.length) return null;
      return <div key={group} style={{ marginBottom: 16 }}>
        <Typography.Title level={5}>{title}</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>{note}</Typography.Paragraph>
        {cols.length === 0 ? <Typography.Text>観測した回数はありません。</Typography.Text>
          : <Table size="small" rowKey="date" pagination={false} scroll={{ x: true }} dataSource={rows}
            columns={[{ title: "日付", width: 160, dataIndex: "date" }, ...cols.map((name) => ({ title: <span title={name}>{missedLabel(name)}</span>, width: Math.max(160, missedLabel(name).length * 14 + 32), key: name, render: (_: unknown, row: { counts: Record<string, number> }) => row.counts[name] ?? 0 }))]} />}
      </div>;
    })}
  </>;
}

/** 送信側の画面へのリンクだけ(送信側の MailHelp の警告・説明は受信には当てはまらないので出さない)。 */
function SendLinks() {
  const me = useMe();
  if (!(hasFeature(me.data, "dlp.mail") && hasRole(me.data, "dlp:viewer"))) return null;
  return <Space wrap style={{ marginBottom: 8 }}><Link to="/dlp/mail/rules">規則</Link><Link to="/dlp/mail/status">状態</Link></Space>;
}

function sameSearch(a: MailInboundSearch | null, b: MailInboundSearch) {
  return !!a && JSON.stringify(a) === JSON.stringify(b);
}

type SearchValues = {
  range: [Dayjs, Dayjs]; sender_domain?: string; recipient?: string; subject?: string;
  attachment: "all" | "yes" | "no" | "unknown"; auth: "all" | "fail" | "unverified";
};

/** 設計書 dlp-inbound-log §1 の文をそのまま出す(README と同じ文)。 */
export function MailInboundHelp() {
  return <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
    <Typography.Paragraph style={{ marginBottom: 0 }}><strong>できる</strong>: 社外から自社のユーザー宛てに届いたメールについて、<strong>Gmail が複製を配送した記録</strong>として、受信時刻・差出人(封筒とヘッダー)・自社側の宛先(Gmail が示す元の封筒宛先)・件名・Message-ID・大きさ・添付の名前と大きさとハッシュ・送信元の認証結果(SPF / DKIM / DMARC。Google が付けた <code>Authentication-Results</code> を写す)を記録し、管理者が期間・差出人ドメイン・宛先・件名・添付の有無で検索できる。</Typography.Paragraph>
    <Typography.Paragraph style={{ marginBottom: 0 }}><strong>「届いた」の意味</strong>: 記録は「Gmail がこのメールを受け取り、記録用の複製を送った」ことを示す。受信者の受信箱に実際に配送されたこと(迷惑メール振り分け・隔離・転送の結果)は示さない(判断点 11)。1 行の単位は <strong>複製 1 通</strong>。P0-I1 で複製が元封筒宛先ごとに分かれるならその単位になる。</Typography.Paragraph>
    <Typography.Paragraph style={{ marginBottom: 0 }}><strong>できない(v1)</strong>: 本文の保存・検索(保存しない。§4)。受信の<strong>阻止・保留</strong>(記録のみ。配送には手を入れない)。社内同士のメール。Gmail のルーティングを通らない経路(POP 取り込み、Gmail API での直接挿入)。<code>mail_max_size_mib</code> を超えるメール(§3.5。観測できた回数だけ分かる)。迷惑メール判定で隔離・破棄されたメール、グループ宛て・エイリアス宛ての扱いは<strong>段 0 で合格した範囲だけを「対象」と書く</strong>(判断点 12)。</Typography.Paragraph>
    <Typography.Paragraph style={{ marginBottom: 0 }}><strong>限界(画面に出す)</strong>: 複製は Gmail の規則が作る。規則が外れれば記録は止まる(§10)。記録の真正性は複製専用の合言葉(§3.3)に依る。宛先・認証結果は<strong>信頼域</strong>(§4.2)が証明できた範囲でだけ「Google が付けた値」として扱う。</Typography.Paragraph>
  </Space>;
}

/** 画面の入力(端末のローカル時刻)→ API の本文。期間は UTC の ISO 文字列にして送る。 */
function toSearch(values: SearchValues, page: number): MailInboundSearch {
  const [from, to] = values.range;
  const text = (value?: string) => value?.trim() || undefined;
  return {
    from: from.toDate().toISOString(), to: to.toDate().toISOString(),
    sender_domain: text(values.sender_domain), recipient: text(values.recipient), subject: text(values.subject),
    has_attachment: values.attachment === "yes" ? true : values.attachment === "no" ? false : values.attachment === "unknown" ? "unknown" : undefined,
    auth_fail: values.auth === "fail" ? true : undefined, unverified: values.auth === "unverified" ? true : undefined,
    page,
  };
}

function AuthTags({ row }: { row: MailInboundItem }) {
  return <Space size={2} wrap>
    <Tag>SPF {row.auth_spf}</Tag><Tag>DKIM {row.auth_dkim}</Tag><Tag>DMARC {row.auth_dmarc}</Tag>
    {row.auth_trust === "google_arc" && <Tag color="blue">ARC 経由で確認</Tag>}
    {row.auth_trust !== "google" && row.auth_trust !== "google_arc" && <Tag color="warning">未検証</Tag>}
  </Space>;
}

function attachmentText(row: MailInboundItem) {
  if (row.attachments_total === null) return `不明(下限 ${row.attachments_total_floor})`;
  return row.attachments_total === row.attachment_count ? `${row.attachments_total}` : `${row.attachments_total}(記録 ${row.attachment_count})`;
}

function StateTags({ row }: { row: MailInboundItem }) {
  return <Space size={2} wrap>
    {row.parse_state !== "ok" && <Tag color="warning">不完全: {row.parse_state}</Tag>}
    {row.anonymized_at && <Tag>匿名化済み</Tag>}
  </Space>;
}

export function MailInboundPage() {
  const api = useApi();
  const scope = useMailScope();
  const [form] = Form.useForm<SearchValues>();
  const [search, setSearch] = useState<MailInboundSearch | null>(null);
  const [rangeError, setRangeError] = useState("");
  const [uid, setUid] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  // 検索ボタンを押したときだけ search が変わる。入力中は問い合わせない
  const result = useQuery({ queryKey: ["dlp", "mail", scope, "inbound", "search", search], gcTime: 0, retry: false,
    queryFn: ({ signal }) => api.mailInboundSearch(search!, signal), enabled: !!search });
  const detail = useQuery({ queryKey: ["dlp", "mail", scope, "inbound", "detail", uid], gcTime: 0, retry: false,
    queryFn: ({ signal }) => api.mailInboundDetail(uid!, signal), enabled: !!uid });
  const stats = useQuery({ queryKey: ["dlp", "mail", scope, "inbound", "stats", days], gcTime: 0, retry: false,
    queryFn: ({ signal }) => api.mailInboundStats(days, signal) });

  function submit(values: SearchValues) {
    const [from, to] = values.range;
    if (!from.isBefore(to)) { setRangeError("期間の開始は終了より前にしてください"); return; }
    if (to.diff(from, "millisecond") > MAX_DAYS * 86_400_000) { setRangeError(`期間は ${MAX_DAYS} 日以内にしてください`); return; }
    setRangeError("");
    setUid(null);
    const next = toSearch(values, 1);
    // 同じ条件だと queryKey が変わらず問い合わせが出ないので、明示的に取り直す(一時的な 503 の後の再検索)
    if (sameSearch(search, next)) void result.refetch(); else setSearch(next);
  }

  const page = result.isError ? undefined : result.data;
  const s = stats.isError ? undefined : stats.data;
  const total = page?.total ?? 0;
  return <div className="ui-list"><div className="ui-list-main">
    <PageHeader title="受信の記録" description="社外から自社のユーザー宛てに届いたメールの、Gmail が送った複製の記録です。閲覧・検索・集計はすべて監査に残ります。" />
    <SendLinks />
    <MailInboundHelp />
    <Typography.Title level={4}>検索</Typography.Title>
    <Form form={form} layout="inline" onFinish={submit} style={{ rowGap: 8, marginBottom: 12 }}
      initialValues={{ range: [dayjs().subtract(7, "day"), dayjs()], attachment: "all", auth: "all" }}>
      <Form.Item name="range" label="期間" rules={[{ required: true, message: "期間を入力してください" }]}>
        <DatePicker.RangePicker showTime aria-label="期間" />
      </Form.Item>
      <Form.Item name="sender_domain" label="差出人ドメイン" rules={[{ max: 255 }]}><Input allowClear placeholder="example.com" /></Form.Item>
      <Form.Item name="recipient" label="宛先" rules={[{ max: 320 }]}><Input allowClear placeholder="user@自社ドメイン" /></Form.Item>
      <Form.Item name="subject" label="件名" rules={[{ max: 100, message: "件名は 100 文字以内にしてください" }]}><Input allowClear maxLength={100} /></Form.Item>
      <Form.Item name="attachment" label="添付"><Select style={{ width: 110 }} options={[{ value: "all", label: "すべて" }, { value: "yes", label: "あり" }, { value: "no", label: "なし" }, { value: "unknown", label: "不明" }]} /></Form.Item>
      <Form.Item name="auth" label="認証"><Select style={{ width: 130 }} options={[{ value: "all", label: "すべて" }, { value: "fail", label: "失敗あり" }, { value: "unverified", label: "未検証" }]} /></Form.Item>
      <Form.Item><Button type="primary" htmlType="submit" loading={result.isFetching}>検索</Button></Form.Item>
    </Form>
    <Typography.Text type="secondary">期間は端末の時刻で入力し、UTC に変換して送ります。開始を含み終了を含みません。宛先はアドレスの完全一致、件名は部分一致です。</Typography.Text>
    {rangeError && <Alert type="error" showIcon message={rangeError} />}
    {result.isError && <Alert type="error" showIcon message={mailError(result.error)} />}
    {search && <>
      <Table<MailInboundItem> scroll={{ x: "max-content" }} dataSource={page?.items ?? []} rowKey="uid" loading={result.isFetching} pagination={false} size="small"
        onRow={(row) => ({ onClick: () => setUid(row.uid), style: { cursor: "pointer" } })} columns={[
          { title: "受信時刻", width: 180, render: (_, row) => dayjs(row.received_at).format("YYYY-MM-DD HH:mm:ss") },
          { title: "差出人", width: 320, render: (_, row) => <Space direction="vertical" size={0}>
            <Typography.Text>{row.header_from_name ?? row.header_from ?? "—"}</Typography.Text>
            {row.header_from_name && row.header_from && <Typography.Text type="secondary">{row.header_from}</Typography.Text>}
            <Typography.Text type="secondary">封筒: {row.mail_from ?? "—"}</Typography.Text>
          </Space> },
          { title: "宛先", width: 320, render: (_, row) => <>{row.recipients.join(", ") || "—"}{row.recipient_source === "unknown" && <> <Tag color="warning">宛先不明</Tag></>}</> },
          { title: "件名", width: 320, ellipsis: true, onCell: (row) => ({ title: row.subject ?? "—" }), render: (_, row) => row.subject ?? "—" },
          { title: "添付", width: 180, render: (_, row) => attachmentText(row) },
          { title: "認証", width: 520, render: (_, row) => <AuthTags row={row} /> },
          { title: "状態", width: 280, render: (_, row) => <StateTags row={row} /> },
        ]} />
      <Space style={{ marginTop: 12 }}>
        <Pagination current={search.page} pageSize={PER_PAGE} total={Math.min(total, MAX_PAGE * PER_PAGE)} showSizeChanger={false}
          onChange={(next) => { setUid(null); setSearch({ ...search, page: next }); }} />
        <Typography.Text>全 {total} 件</Typography.Text>
        {total > MAX_PAGE * PER_PAGE && <Typography.Text type="warning">表示できるのは 10,000 件(200 頁)まで。期間や条件を絞ってください</Typography.Text>}
      </Space>
    </>}
    <Typography.Title level={4} style={{ marginTop: 24 }}>日ごとの集計(UTC の日付)</Typography.Title>
    <Select aria-label="集計の日数" value={days} style={{ width: 120, marginBottom: 12 }} onChange={setDays}
      options={[7, 30, 90].map((n) => ({ value: n, label: `直近 ${n} 日` }))} />
    {stats.isError && <Alert type="error" showIcon message={mailError(stats.error)} />}
    {s && <>
      <Table scroll={{ x: "max-content" }} size="small" rowKey="date" dataSource={[...s.items].reverse()} loading={stats.isFetching} pagination={false} columns={[
        { title: "日付", width: 120, dataIndex: "date" }, { title: "件数", width: 90, dataIndex: "count" }, { title: "DMARC 合格以外", width: 172, dataIndex: "dmarc_not_pass" },
        { title: "未検証", width: 160, dataIndex: "unverified" }, { title: "宛先不明", width: 240, dataIndex: "recipient_unknown" }, { title: "不完全な記録", width: 160, dataIndex: "parse_not_ok" },
      ]} />
      <MissedTables stats={s} />
    </>}
  </div>{uid && <SidePanel title="受信の詳細" onClose={() => setUid(null)} width={680}>
    {detail.isError && <Alert type="error" showIcon message={mailError(detail.error)} />}
    {detail.isFetching ? <Typography.Text>詳細を取得中…</Typography.Text> : !detail.isError && detail.data && <InboundPanel row={detail.data} />}
  </SidePanel>}</div>;
}

function InboundPanel({ row }: { row: MailInboundDetail }) {
  const list = (values: string[]) => values.join(", ") || "—";
  return <Space direction="vertical" style={{ width: "100%" }} data-testid="inbound-detail">
    <Typography.Title level={4}>{row.subject ?? "件名なし"}</Typography.Title>
    <Space wrap><AuthTags row={row} /><StateTags row={row} /></Space>
    <Descriptions column={1} bordered size="small" items={[
      { key: "uid", label: "uid", children: row.uid },
      { key: "received", label: "受信時刻", children: dayjs(row.received_at).format("YYYY-MM-DD HH:mm:ss") },
      { key: "mail_from", label: "封筒の差出人", children: row.mail_from ?? "—" },
      { key: "sender_domain", label: "差出人ドメイン", children: row.sender_domain ?? "—" },
      { key: "header_from", label: "From ヘッダー", children: `${row.header_from_name ?? ""} ${row.header_from ? `<${row.header_from}>` : ""}`.trim() || "—" },
      { key: "header_from_domain", label: "From のドメイン", children: row.header_from_domain ?? "—" },
      { key: "recipients", label: "宛先", children: <>{list(row.recipients)} {row.recipient_source === "unknown" ? <Tag color="warning">宛先不明</Tag> : <Tag>{row.recipient_source}</Tag>}</> },
      { key: "header_recipients", label: "ヘッダーの宛先", children: list(row.header_recipients) },
      { key: "subject", label: "件名", children: row.subject ?? "—" },
      { key: "date_header", label: "Date ヘッダー", children: row.date_header ?? "—" },
      { key: "message_id", label: "Message-ID", children: row.message_id_header ?? "—" },
      { key: "size", label: "大きさ(バイト)", children: row.size_bytes },
      { key: "attachments", label: "添付の数", children: attachmentText(row) },
      { key: "auth", label: "認証結果", children: `SPF ${row.auth_spf} / DKIM ${row.auth_dkim} / DMARC ${row.auth_dmarc}` },
      { key: "dkim_domains", label: "DKIM の署名ドメイン", children: list(row.auth_dkim_domains) },
      { key: "trust", label: "認証結果の出どころ", children: row.auth_trust },
      { key: "spam", label: "迷惑メールの印", children: row.spam_flag === null ? "—" : row.spam_flag ? "あり" : "なし" },
      { key: "parse_state", label: "解析の状態", children: row.parse_state },
      { key: "flags", label: "印", children: row.flags.length ? <Space size={2} wrap>{row.flags.map((f) => <Tag key={f}>{f}</Tag>)}</Space> : "—" },
      { key: "anonymized", label: "匿名化", children: row.anonymized_at ? dayjs(row.anonymized_at).format("YYYY-MM-DD HH:mm:ss") : "—" },
    ]} />
    <Table scroll={{ x: "max-content" }} size="small" rowKey={(_, i) => String(i)} dataSource={row.attachments} pagination={false} columns={[
      { title: "添付の名前", width: 240, render: (_, a) => a.name ?? "—" }, { title: "種類", width: 240, dataIndex: "content_type" },
      { title: "大きさ", width: 90, render: (_, a) => a.size ?? "不明" },
      { title: "SHA-256", width: 600, render: (_, a) => a.sha256 ? <Typography.Text code copyable style={{ wordBreak: "break-all" }}>{a.sha256}</Typography.Text> : "不明" },
      { title: "状態", width: 110, dataIndex: "state" },
    ]} />
  </Space>;
}
